import type { TFunction } from 'i18next';
import { camelCase, isEqual, sortBy, upperFirst } from 'lodash';

import { GROUP_SEPARATOR } from '../pages/Assistance/useDocumentConfig';

export interface FieldOption {
    name: string;
    value?: unknown;
    defaultValue?: unknown;
    valueType?: string;
    isNullable?: boolean;
    configurable?: boolean;
}

export interface FieldDefinition {
    name: string;
    enabled?: boolean;
    position?: number;
    options?: FieldOption[];
}

// what the backend saves: a field by its full name with its option values
export interface FieldInput {
    fieldName: string;
    position?: number;
    options: { name: string; value: unknown }[];
}

// a field's option values by the field's first name part (its group), then by the field's name
export interface FieldsFormValues {
    [group: string]: Record<string, Record<string, unknown>> | undefined;
}

export const DEFAULT_CUSTOM_FIELD_OPTIONS: FieldOption[] = [
    { name: 'is_required', valueType: 'boolean', defaultValue: false },
    { name: 'data_label_name', valueType: 'string', defaultValue: null },
    { name: 'data_type', valueType: 'string', defaultValue: 'string' },
    { name: 'choices', valueType: 'list', defaultValue: null },
    { name: 'field_label_name', valueType: 'string', defaultValue: null },
    { name: 'data_label_instructions', valueType: 'string', defaultValue: null },
];

// a custom field not saved yet; the backend renames it after its data_label_name
export const NEW_CUSTOM_FIELD_PREFIX = 'custom_field_';

export const groupOf = (name: string) => name.split(GROUP_SEPARATOR, 1)[0];

export const isCustomFieldDefinition = (field: FieldDefinition) =>
    field.name.startsWith(NEW_CUSTOM_FIELD_PREFIX) ||
    !!field.options?.some((option) => option.name === 'field_label_name');

// a universal table: its fields are "<table>__<field>"
export const isGroupField = (field: FieldDefinition) =>
    !!field.options?.some((option) => option.name === 'group' && option.value === true);

const isConfigurable = (option: FieldOption) => option.configurable !== false;

const optionValuesOf = (field: FieldDefinition) =>
    Object.fromEntries((field.options ?? []).filter(isConfigurable).map((option) => [option.name, option.value]));

export const convertFieldsToFormData = (fields: FieldDefinition[]): FieldsFormValues => {
    const formData: FieldsFormValues = {};
    fields.forEach((field) => {
        const group = groupOf(field.name);
        formData[group] = { ...formData[group], [field.name]: optionValuesOf(field) };
    });
    return formData;
};

// fullNameOf turns the editor's name of a field (without its item type) back into the name the backend knows
export const convertFormDataToFieldInputs = (
    formData: FieldsFormValues,
    fieldsSort: string[],
    fullNameOf: (name: string) => string
): FieldInput[] =>
    Object.values(formData)
        .flatMap((group) => Object.entries(group ?? {}))
        .map(([name, values]) => ({
            fieldName: fullNameOf(name),
            position: fieldsSort.includes(name) ? fieldsSort.indexOf(name) : undefined,
            options: Object.entries(values).map(([optionName, value]) => ({ name: optionName, value })),
        }));

export const convertFieldsToEnabledFieldInputs = (fields: FieldDefinition[] = []): FieldInput[] =>
    fields
        .filter((field) => field.enabled)
        .map((field) => ({
            fieldName: field.name,
            position: field.position,
            options: (field.options ?? [])
                .filter(isConfigurable)
                .map((option) => ({ name: option.name, value: option.value })),
        }));

export const byPosition = (a: FieldDefinition, b: FieldDefinition) => {
    if (a.position === undefined && b.position === undefined) return 0;
    if (a.position === undefined) return 1;
    if (b.position === undefined) return -1;
    return a.position - b.position;
};

// a field-specific label or help text (e.g. priceUnitMappingHelpText) wins over the one shared by all fields with that
// option
export const getOptionLabel = (t: TFunction, fieldName: string, name: string): string =>
    t([
        `configPage.document.form.${camelCase(fieldName)}${upperFirst(camelCase(name))}`,
        `configPage.document.form.${camelCase(name)}`,
    ]);

export const getOptionHelpText = (t: TFunction, fieldName: string, name: string): string | undefined =>
    t(
        [
            `configPage.document.form.${camelCase(fieldName)}${upperFirst(camelCase(name))}HelpText`,
            `configPage.document.form.${camelCase(name)}HelpText`,
        ],
        { defaultValue: '' }
    ) || undefined;

// a field's name as the editor shows it: its display name (field_label_name for custom fields), else its default.
// With form values, typing, clearing or switching off a name shows right away; without them it's the saved name.
export const getFieldLabel = (
    t: TFunction,
    key: string,
    fields: FieldDefinition[],
    formValues: FieldsFormValues | undefined,
    translationPrefix: string
): string => {
    const field = fields.find((item) => item.name === key);
    const formOptions = formValues?.[groupOf(key)]?.[key];
    const valueOf = (name: string) =>
        formOptions ? formOptions[name] : field?.options?.find((option) => option.name === name)?.value;

    if (field && isCustomFieldDefinition(field)) {
        return (valueOf('field_label_name') as string) || t('configPage.document.form.untitled');
    }
    return (valueOf('display_name') as string) || t(`${translationPrefix}.${key}`);
};

// the options the editor shows: ones the backend knows and lets the user set, under their conditions
export const isShownOption = (name: string, values: Record<string, unknown>, field: FieldDefinition) => {
    const option = field.options?.find((item) => item.name === name);
    if (!option || !isConfigurable(option) || name === 'default_field_value') return false;
    if (name === 'choices') return values.data_type === 'string';
    // the backend only stores it together with a maximum length (save_field_config.py)
    if (name === 'enforce_assistance_on_max_field_length') return values.max_field_length != undefined;
    return true;
};

export const isShownSavedOption = (option: FieldOption, field: FieldDefinition) =>
    isShownOption(option.name, optionValuesOf(field), field);

const BASIC_FIELD_OPTIONS = ['is_required', 'display_name'];
// a custom field's "Display name" is field_label_name
const BASIC_CUSTOM_FIELD_OPTIONS = ['is_required', 'field_label_name'];
// the settings a custom field can't be saved without
export const REQUIRED_CUSTOM_FIELD_OPTIONS = [
    'field_label_name',
    'data_label_name',
    'data_type',
    'data_label_instructions',
];

// the advanced ones keep the backend's order
export const groupFieldOptions = (field: FieldDefinition, values: Record<string, unknown>) => {
    const basicNames = isCustomFieldDefinition(field) ? BASIC_CUSTOM_FIELD_OPTIONS : BASIC_FIELD_OPTIONS;
    const shown = Object.keys(values).filter((name) => isShownOption(name, values, field));
    return {
        basic: sortBy(
            shown.filter((name) => basicNames.includes(name)),
            (name) => basicNames.indexOf(name)
        ),
        advanced: shown.filter((name) => !basicNames.includes(name)),
    };
};

// the saved settings of a field that differ from their defaults, except Required, which the row note says anyway; a
// custom field's settings have no defaults to differ from
export const changedSettingsCount = (fields: FieldDefinition[]) =>
    fields
        .filter((field) => !isCustomFieldDefinition(field))
        .flatMap((field) =>
            (field.options ?? []).filter(
                (option) =>
                    option.name !== 'is_required' &&
                    isShownSavedOption(option, field) &&
                    !isEqual(option.value ?? null, option.defaultValue ?? null)
            )
        ).length;

// moves a field with its subfields, or a subfield among its siblings, to where `target` is
export const moveField = (sort: string[], name: string, target: string) => {
    if (name === target) return sort;
    const blockOf = (item: string) =>
        item.includes(GROUP_SEPARATOR)
            ? [item]
            : [item, ...sort.filter((f) => f.startsWith(`${item}${GROUP_SEPARATOR}`))];
    const moving = blockOf(name);
    const rest = sort.filter((f) => !moving.includes(f));
    const isDown = sort.indexOf(name) < sort.indexOf(target);
    const index = rest.indexOf(target) + (isDown ? blockOf(target).length : 0);
    return [...rest.slice(0, index), ...moving, ...rest.slice(index)];
};
